import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCamera } from "../context/CameraContext";

// ─── Lucide-style SVG icons (inline, no dependencies) ────────────────────────
const iconBase = {
  width: 18, height: 18, viewBox: "0 0 24 24",
  fill: "none", stroke: "currentColor",
  strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round",
};

const CameraIcon = (props) => (
  <svg {...iconBase} {...props}>
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
    <circle cx="12" cy="13" r="3" />
  </svg>
);

const ApertureIcon = (props) => (
  <svg {...iconBase} {...props}>
    <circle cx="12" cy="12" r="10" />
    <path d="M14.31 8l5.74 9.94" />
    <path d="M9.69 8h11.48" />
    <path d="M7.38 12l5.74-9.94" />
    <path d="M9.69 16L3.95 6.06" />
    <path d="M14.31 16H2.83" />
    <path d="M16.62 12l-5.74 9.94" />
  </svg>
);

const MonitorIcon = (props) => (
  <svg {...iconBase} {...props}>
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const CompassIcon = (props) => (
  <svg {...iconBase} {...props}>
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);

const ActivityIcon = (props) => (
  <svg {...iconBase} {...props}>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const CheckIcon = (props) => (
  <svg {...iconBase} {...props}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = (props) => (
  <svg {...iconBase} {...props}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ShieldCheckIcon = (props) => (
  <svg {...iconBase} {...props}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const AlertTriangleIcon = (props) => (
  <svg {...iconBase} {...props}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const SpinnerIcon = (props) => (
  <svg {...iconBase} {...props}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const DEEPREAL_BUILD_ID = "DR-2026-05-07-05-dynamic-screen-guard";

const requiresSensorPermission = () =>
  typeof window !== "undefined" &&
  (
    typeof window.DeviceOrientationEvent?.requestPermission === "function" ||
    typeof window.DeviceMotionEvent?.requestPermission === "function"
  );

const SENSOR_TIMEOUT_MS = 4000;
const FRAME_TIMEOUT_MS = 2500;

const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);

const hasOrientationReading = (event) =>
  [event?.alpha, event?.beta, event?.gamma].some(isFiniteNumber);

const objectHasFiniteNumber = (obj) =>
  !!obj && Object.values(obj).some(isFiniteNumber);

const hasMotionReading = (event) =>
  objectHasFiniteNumber(event?.acceleration) ||
  objectHasFiniteNumber(event?.accelerationIncludingGravity) ||
  objectHasFiniteNumber(event?.rotationRate);

const summarizeOrientationEvent = (event, eventName) => ({
  eventName,
  alpha: event?.alpha ?? null,
  beta: event?.beta ?? null,
  gamma: event?.gamma ?? null,
  absolute: event?.absolute ?? null,
});

const summarizeMotionEvent = (event, eventName) => ({
  eventName,
  acceleration: event?.acceleration
    ? {
        x: event.acceleration.x ?? null,
        y: event.acceleration.y ?? null,
        z: event.acceleration.z ?? null,
      }
    : null,
  accelerationIncludingGravity: event?.accelerationIncludingGravity
    ? {
        x: event.accelerationIncludingGravity.x ?? null,
        y: event.accelerationIncludingGravity.y ?? null,
        z: event.accelerationIncludingGravity.z ?? null,
      }
    : null,
  rotationRate: event?.rotationRate
    ? {
        alpha: event.rotationRate.alpha ?? null,
        beta: event.rotationRate.beta ?? null,
        gamma: event.rotationRate.gamma ?? null,
      }
    : null,
});

const requestSensorPermission = async (SensorEvent) => {
  if (typeof SensorEvent?.requestPermission !== "function") return true;
  const result = await SensorEvent.requestPermission();
  return result === "granted";
};

const getPermissionPolicyAllowed = (feature) => {
  try {
    const policy = document.permissionsPolicy || document.featurePolicy;
    if (!policy?.allowsFeature) return "unknown";
    return policy.allowsFeature(feature);
  } catch {
    return "unknown";
  }
};

const getSensorDiagnostics = () => ({
  isSecureContext: window.isSecureContext,
  userAgent: window.navigator.userAgent,
  hasDeviceOrientationEvent: typeof window.DeviceOrientationEvent !== "undefined",
  hasDeviceMotionEvent: typeof window.DeviceMotionEvent !== "undefined",
  hasDeviceOrientationPermission:
    typeof window.DeviceOrientationEvent?.requestPermission === "function",
  hasDeviceMotionPermission:
    typeof window.DeviceMotionEvent?.requestPermission === "function",
  hasOnDeviceOrientation: "ondeviceorientation" in window,
  hasOnDeviceOrientationAbsolute: "ondeviceorientationabsolute" in window,
  hasOnDeviceMotion: "ondevicemotion" in window,
  accelerometerPolicy: getPermissionPolicyAllowed("accelerometer"),
  gyroscopePolicy: getPermissionPolicyAllowed("gyroscope"),
});

const waitForSensorEvent = ({
  eventNames,
  eventName,
  isValid,
  summarizeEvent,
  timeoutMs = SENSOR_TIMEOUT_MS,
}) =>
  new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve({ ok: false, reason: "window-unavailable", eventsSeen: 0, invalidEventsSeen: 0 });
      return;
    }

    const names = eventNames || [eventName];
    let settled = false;
    let timeoutId = null;
    let eventsSeen = 0;
    let invalidEventsSeen = 0;
    let firstInvalidSample = null;

    const cleanup = () => {
      names.forEach((name) => window.removeEventListener(name, handleEvent));
      if (timeoutId) window.clearTimeout(timeoutId);
    };

    const finish = (ok, reason, sourceEventName = null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ ok, reason, sourceEventName, eventsSeen, invalidEventsSeen, firstInvalidSample });
    };

    const handleEvent = (event) => {
      eventsSeen += 1;
      if (isValid(event)) {
        finish(true, "real-event", event.type);
      } else {
        invalidEventsSeen += 1;
        if (!firstInvalidSample && typeof summarizeEvent === "function") {
          firstInvalidSample = summarizeEvent(event, event.type);
        }
      }
    };

    names.forEach((name) => window.addEventListener(name, handleEvent, { passive: true }));
    timeoutId = window.setTimeout(() => finish(false, "timeout-no-real-event"), timeoutMs);
  });

const waitForVideoMetadata = (stream, timeoutMs = FRAME_TIMEOUT_MS) =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;

    let settled = false;
    let timeoutId = null;

    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
      if (timeoutId) window.clearTimeout(timeoutId);
      video.pause();
      video.srcObject = null;
      video.remove();
    };

    const finish = (ok) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(ok);
    };

    video.onloadedmetadata = () => {
      finish(video.videoWidth > 0 && video.videoHeight > 0);
    };
    video.onerror = () => finish(false);
    timeoutId = window.setTimeout(() => finish(false), timeoutMs);
    video.srcObject = stream;
    video.play?.().catch(() => {
      // loadedmetadata can still fire even when autoplay is blocked.
    });
  });

const streamHasVideoFrame = async (stream) => {
  const videoTrack = stream?.getVideoTracks?.()[0];
  const settings = videoTrack?.getSettings?.();
  if (settings && settings.width > 0 && settings.height > 0) return true;
  return waitForVideoMetadata(stream);
};

// ─── Status pill (right side of each row) ─────────────────────────────────────
const StatusPill = ({ value, pendingLabel = "Checking", falseVariant = "error" }) => {
  if (value === null) {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 9px", borderRadius: 999,
        background: "rgba(100,116,139,0.18)", border: "1px solid rgba(100,116,139,0.45)",
        color: "#94a3b8", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.02em",
      }}>
        <SpinnerIcon width={12} height={12} style={{ animation: "drcheck-spin 0.9s linear infinite" }} />
        {pendingLabel}
      </span>
    );
  }
  if (value === true) {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 9px", borderRadius: 999,
        background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.5)",
        color: "#86efac", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.02em",
      }}>
        <CheckIcon width={12} height={12} />
      </span>
    );
  }
  const isWarning = falseVariant === "warning";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 9px", borderRadius: 999,
      background: isWarning ? "rgba(245,158,11,0.15)" : "rgba(239,68,68,0.15)",
      border: isWarning ? "1px solid rgba(245,158,11,0.55)" : "1px solid rgba(239,68,68,0.5)",
      color: isWarning ? "#fde68a" : "#fca5a5",
      fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.02em",
    }}>
      {isWarning ? <AlertTriangleIcon width={12} height={12} /> : <XIcon width={12} height={12} />}
      {isWarning ? "Limited" : "Unavailable"}
    </span>
  );
};

// ─── Single capability row ────────────────────────────────────────────────────
const CapabilityRow = ({ icon: Icon, label, value, pendingLabel, falseVariant, children }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 4px", gap: 10,
    borderTop: "1px solid rgba(51,65,85,0.5)",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <span style={{ color: "#94a3b8", display: "inline-flex" }}>
        <Icon />
      </span>
      <span style={{
        color: "#e2e8f0", fontSize: "0.88rem", fontWeight: 500,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </span>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
      {children}
      <StatusPill value={value} pendingLabel={pendingLabel} falseVariant={falseVariant} />
    </div>
  </div>
);

const DeviceCheck = ({ onCheckComplete }) => {
  const { detectCapabilities } = useCamera();

  const [cameraSupported, setCameraSupported] = useState(null);
  const [orientationSupported, setOrientationSupported] = useState(null);
  const [motionSupported, setMotionSupported] = useState(null);
  const [webglSupported, setWebglSupported] = useState(null);
  const [rearCameraSupported, setRearCameraSupported] = useState(null);
  const [videoFrameSupported, setVideoFrameSupported] = useState(null);
  const [sensorCheckRunning, setSensorCheckRunning] = useState(false);
  const sensorPermissionRequestedRef = useRef(false);
  const sensorPermissionGrantedRef = useRef(false);

  useEffect(() => {
    const canvas = document.createElement("canvas");
    let gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    const hasWebGL = !!gl;
    setWebglSupported(hasWebGL);

    // Release the probe context immediately so capability checks do not
    // accumulate Chromium WebGL contexts across rerenders/remounts.
    try {
      gl?.getExtension("WEBGL_lose_context")?.loseContext();
    } catch {
      // Capability probe only; ignore cleanup failures.
    }
    gl = null;

    const hasCameraAPI = !!navigator.mediaDevices?.getUserMedia;
    setCameraSupported(hasCameraAPI);

    detectCapabilities?.();
    // Run the capability probe only once per mount. The camera context may
    // rerender frequently, but rerunning this effect would reopen WebGL
    // probe contexts and pollute browser-comparison tests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rear-camera probe (v3-style).
  // Uses facingMode: { exact: 'environment' } — fails with OverconstrainedError
  // on devices without a rear camera (e.g. laptops/desktops), correctly
  // reporting rearCameraSupported = false. Runs only after the basic camera
  // API check has resolved. Tracks are stopped BEFORE setState to release the
  // hardware lock as early as possible (important on mobile where the main
  // pipeline will call startCamera() later via user interaction).
  useEffect(() => {
    if (cameraSupported === null) return;
    if (cameraSupported === false) {
      setRearCameraSupported(false);
      setVideoFrameSupported(false);
      return;
    }

    let cancelled = false;
    const probeRearCamera = async () => {
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: "environment" } },
        });
        const hasVideoFrame = await streamHasVideoFrame(testStream);
        testStream.getTracks().forEach((t) => t.stop());
        if (!cancelled) {
          setRearCameraSupported(true);
          setVideoFrameSupported(hasVideoFrame);
        }
      } catch (err) {
        console.log("[DeviceCheck] Rear camera probe failed:", err?.name || err);
        if (!cancelled) {
          setRearCameraSupported(false);
          setVideoFrameSupported(false);
        }
      }
    };

    probeRearCamera();
    return () => { cancelled = true; };
  }, [cameraSupported]);

  const runSensorChecks = async () => {
    if (sensorCheckRunning) return;
    const needsPermission = requiresSensorPermission();
    const canRequestPermission =
      !needsPermission ||
      sensorPermissionGrantedRef.current ||
      !sensorPermissionRequestedRef.current;
    if (!canRequestPermission) return;

    setSensorCheckRunning(true);
    sensorPermissionRequestedRef.current = true;
    setOrientationSupported(null);
    setMotionSupported(null);

    try {
      const diagnostics = getSensorDiagnostics();
      console.log("[DeviceCheck] Sensor environment", diagnostics);

      const [orientationPermissionOk, motionPermissionOk] = sensorPermissionGrantedRef.current
        ? [true, true]
        : await Promise.all([
            requestSensorPermission(window.DeviceOrientationEvent),
            requestSensorPermission(window.DeviceMotionEvent),
          ]);

      sensorPermissionGrantedRef.current = orientationPermissionOk && motionPermissionOk;

      if (!orientationPermissionOk || !motionPermissionOk) {
        setOrientationSupported(orientationPermissionOk ? null : false);
        setMotionSupported(motionPermissionOk ? null : false);
      }

      const [orientationResult, motionResult] = await Promise.all([
        orientationPermissionOk
          ? waitForSensorEvent({
              eventNames: ["deviceorientation", "deviceorientationabsolute"],
              isValid: hasOrientationReading,
              summarizeEvent: summarizeOrientationEvent,
            })
          : Promise.resolve({ ok: false, reason: "permission-denied" }),
        motionPermissionOk
          ? waitForSensorEvent({
              eventName: "devicemotion",
              isValid: hasMotionReading,
              summarizeEvent: summarizeMotionEvent,
            })
          : Promise.resolve({ ok: false, reason: "permission-denied" }),
      ]);

      console.log("[DeviceCheck] Sensor probe", {
        orientation: orientationResult.reason,
        orientationSource: orientationResult.sourceEventName,
        orientationEventsSeen: orientationResult.eventsSeen,
        orientationInvalidEventsSeen: orientationResult.invalidEventsSeen,
        orientationFirstInvalidSample: orientationResult.firstInvalidSample,
        motion: motionResult.reason,
        motionSource: motionResult.sourceEventName,
        motionEventsSeen: motionResult.eventsSeen,
        motionInvalidEventsSeen: motionResult.invalidEventsSeen,
        motionFirstInvalidSample: motionResult.firstInvalidSample,
        timeoutMs: SENSOR_TIMEOUT_MS,
      });

      setOrientationSupported(orientationResult.ok);
      setMotionSupported(motionResult.ok);
    } catch (err) {
      console.error("iOS sensor permission error:", err);
      setOrientationSupported(false);
      setMotionSupported(false);
    } finally {
      setSensorCheckRunning(false);
    }
  };

  // Auto-run sensor checks on mount — no manual button required.
  // On iOS, requestPermission() outside a user gesture is rejected gracefully;
  // sensors show as Limited but do NOT block the app (isCompatible = camera + WebGL only).
  useEffect(() => {
    console.log("[DeepReal] Build:", DEEPREAL_BUILD_ID);
    runSensorChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sensorPendingLabel = sensorCheckRunning ? "Checking" : "Pending";

  const requiredChecksReady =
    cameraSupported !== null &&
    webglSupported !== null &&
    rearCameraSupported !== null &&
    videoFrameSupported !== null;

  const sensorsChecked =
    orientationSupported !== null &&
    motionSupported !== null;

  const hasSensorWarning =
    orientationSupported === false ||
    motionSupported === false;

  const isCompatible = useMemo(() => {
    return (
      cameraSupported &&
      webglSupported &&
      rearCameraSupported &&
      videoFrameSupported
    );
  }, [
    cameraSupported,
    orientationSupported,
    motionSupported,
    webglSupported,
    rearCameraSupported,
    videoFrameSupported,
  ]);

  useEffect(() => {
    if (
      cameraSupported === null ||
      webglSupported === null ||
      rearCameraSupported === null ||
      videoFrameSupported === null
    ) {
      return;
    }
    onCheckComplete?.(isCompatible);
  }, [
    cameraSupported,
    webglSupported,
    rearCameraSupported,
    videoFrameSupported,
    isCompatible,
    onCheckComplete,
  ]);

  const allChecked = requiredChecksReady;

  return (
    <div className="notranslate" translate="no" style={{ color: "#e2e8f0" }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <CapabilityRow icon={CameraIcon}  label="Camera"             value={cameraSupported} />
        <CapabilityRow icon={ApertureIcon} label="Rear Camera"        value={rearCameraSupported} />
        <CapabilityRow icon={CameraIcon}  label="Video Frame"         value={videoFrameSupported} />
        <CapabilityRow icon={MonitorIcon}  label="Graphics (WebGL)"   value={webglSupported} />
        <CapabilityRow
          icon={CompassIcon}
          label="Orientation Sensor"
          value={orientationSupported}
          pendingLabel={sensorPendingLabel}
          falseVariant="warning"
        />
        <CapabilityRow
          icon={ActivityIcon}
          label="Motion Sensor"
          value={motionSupported}
          pendingLabel={sensorPendingLabel}
          falseVariant="warning"
        />
      </div>

      {sensorsChecked && hasSensorWarning && isCompatible && (
        <div style={{
          marginTop: "0.85rem", padding: "0.75rem 0.85rem",
          border: "1px solid rgba(245,158,11,0.55)",
          background: "rgba(245,158,11,0.12)",
          color: "#fde68a", borderRadius: 8,
          display: "flex", alignItems: "flex-start", gap: 10,
        }}>
          <span style={{ color: "#fde68a", display: "inline-flex", marginTop: 1 }}>
            <AlertTriangleIcon width={16} height={16} />
          </span>
          <span style={{ fontSize: "0.82rem", lineHeight: 1.45, fontWeight: 600 }}>
            AI and motion-based analysis can continue, but this device does not provide full motion/orientation data to the browser. Verification may be less stable and may require slower movement or repeated attempts.
          </span>
        </div>
      )}

      {allChecked && (
        <div style={{
          marginTop: "0.85rem", paddingTop: "0.75rem",
          borderTop: "1px solid rgba(51,65,85,0.5)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          {isCompatible ? (
            <>
              <span style={{ color: "#86efac", display: "inline-flex" }}>
                <ShieldCheckIcon />
              </span>
              <span style={{ color: "#86efac", fontSize: "0.86rem", fontWeight: 700 }}>
                Device ready
              </span>
            </>
          ) : (
            <>
              <span style={{ color: "#fca5a5", display: "inline-flex" }}>
                <AlertTriangleIcon />
              </span>
              <span style={{ color: "#fca5a5", fontSize: "0.86rem", fontWeight: 700 }}>
                One or more capabilities are missing
              </span>
            </>
          )}
        </div>
      )}

      {/* Build reference — visible immediately on device compatibility screen */}
      <div style={{
        marginTop: "0.6rem", fontSize: "0.62rem", color: "#334155",
        letterSpacing: "0.03em", userSelect: "none",
      }}>
        Build: {DEEPREAL_BUILD_ID}
      </div>
    </div>
  );
};

export default DeviceCheck;
